/* =============================================================
   MAP SERVICE
   =============================================================
   The one place that knows how to build a map. Every map in the
   app goes through `createMap` -- the search map, an owner's
   locations, the single pin on a berth page, the pin picker on
   the location form -- so tiles, attribution, zoom limits and
   marker styling are decided once.

   It knows nothing about berths. It takes `MapPoint`s: a thing
   with a position, a look, and somewhere to go when clicked.

   Started as a copy of admin/src/lib/map.ts (the repo copies
   shared code between front-ends rather than packaging it) and
   grew what the customer app needs: price labels on pins, a
   muted and a selected look, re-fitting only when the set of
   points changes, and picking a position by clicking the map.

   Leaflet rather than MapLibre or Google: no API key, no billing
   account, no vector style to host, and it bundles, so
   `script-src 'self'` in the CSP stays as it is.
   ============================================================= */

import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { mapConfig } from '@/config/map.config'

export interface MapPoint {
  /** Stable identity — used to tell markers apart across updates. */
  id: number | string
  lat: number
  lng: number
  /** Shown on hover, and read out as the marker's accessible name. */
  title: string
  /** Second line in the hover tooltip. */
  subtitle?: string
  /** Text on the pin itself -- a price -- instead of a plain dot. */
  label?: string
  /** `muted`: does not suit (greyed). `selected`: the one being looked at. */
  tone?: 'default' | 'muted' | 'selected'
  /**
   * Whether the initial view is framed around this point (default true).
   * Search frames the place searched for and leaves outliers 20 km away
   * on the map but out of the opening view.
   */
  frame?: boolean
}

export interface MapHandle {
  /**
   * Replace every marker. The view re-fits only when the SET of points
   * changed -- a point changing tone (being selected) must not make the map
   * jump away from where the user has panned to.
   */
  setPoints(points: MapPoint[]): void
  /** Leaflet mis-sizes itself if the container was hidden when it was made. */
  resize(): void
  destroy(): void
}

export interface CreateMapOptions {
  onSelect?(point: MapPoint): void
  /** Makes the map a picker: a click anywhere reports its position. */
  onPick?(lat: number, lng: number): void
  /** Mouse-wheel zoom. Off for maps inside a scrolling page. */
  scrollWheelZoom?: boolean
}

/**
 * A `divIcon`, not Leaflet's default marker.
 *
 * The default icon resolves its PNGs from a CSS-relative path, which breaks
 * under every bundler and is the single most common Leaflet bug. A div styled
 * from our own tokens has no assets to lose, themes itself, and stays crisp at
 * any density.
 */
function pin(point: MapPoint): L.DivIcon {
  const tone = point.tone ?? 'default'
  if (point.label) {
    return L.divIcon({
      className: `ra-pill ra-pill--${tone}`,
      html: `<span class="ra-pill__text">${escapeHtml(point.label)}</span>`,
      // Width follows the text; Leaflet needs a box, so give it one that the
      // CSS centres the pill in.
      iconSize: [96, 30],
      iconAnchor: [48, 15],
      tooltipAnchor: [0, -16],
    })
  }
  return L.divIcon({
    className: `ra-pin ra-pin--${tone}`,
    html: '<span class="ra-pin__dot"></span>',
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    tooltipAnchor: [0, -10],
  })
}

export function createMap(container: HTMLElement, options: CreateMapOptions = {}): MapHandle {
  const map = L.map(container, {
    scrollWheelZoom: options.scrollWheelZoom ?? false,
    zoomControl: true,
    attributionControl: true,
  }).setView([mapConfig.fallbackCentre.lat, mapConfig.fallbackCentre.lng], mapConfig.fallbackZoom)

  L.tileLayer(mapConfig.tiles.url, {
    attribution: mapConfig.tiles.attribution,
    maxZoom: mapConfig.tiles.maxZoom,
  }).addTo(map)

  // Ctrl/⌘ + wheel still zooms, which is the gesture people expect from a map
  // that otherwise lets the page scroll.
  map.on('wheel', (event) => {
    const original = (event as L.LeafletMouseEvent).originalEvent as WheelEvent
    if (options.scrollWheelZoom) return
    if (original.ctrlKey || original.metaKey) {
      original.preventDefault()
      map.setZoom(map.getZoom() - Math.sign(original.deltaY))
    }
  })

  if (options.onPick) {
    container.classList.add('ra-picking')
    map.on('click', (event) => options.onPick?.(event.latlng.lat, event.latlng.lng))
  }

  const layer = L.layerGroup().addTo(map)

  // Scripted demo recordings and browser tests click a known position on the
  // map ("drop the pin here"), which needs the map's own projection. The
  // instance hangs off its container for them; nothing in the app reads it.
  ;(container as HTMLElement & { raMap?: L.Map }).raMap = map

  /*
   * Leaflet keeps no record of whether it has been removed, and every method
   * on a removed map reaches for panes that are gone — the symptom is
   * `Cannot read properties of undefined (reading '_leaflet_pos')`. A
   * ResizeObserver callback already queued still runs after `disconnect()`,
   * so this flag makes every entry point a no-op once destroyed.
   */
  let destroyed = false
  let fittedTo = ''
  let lastBounds: L.LatLngBounds | null = null
  /* Once someone has dragged or zoomed the map themselves, a resize must not
     snap it back to the fitted view; until then, it should -- a map often
     gets its real size only after its first fit (a sticky column, a tab). */
  let userMoved = false
  map.on('dragstart', () => (userMoved = true))
  map.on('zoomstart', (event) => {
    if ((event as unknown as { originalEvent?: Event }).originalEvent) userMoved = true
  })
  container.addEventListener('wheel', () => (userMoved = true), { passive: true })
  map.zoomControl?.getContainer()?.addEventListener('click', () => (userMoved = true))

  function fit(): void {
    if (!lastBounds) return
    map.fitBounds(lastBounds, {
      padding: [mapConfig.fitPadding, mapConfig.fitPadding],
      maxZoom: mapConfig.maxFitZoom,
      animate: false,
    })
  }

  function setPoints(points: MapPoint[]): void {
    if (destroyed) return
    layer.clearLayers()

    for (const point of points) {
      const marker = L.marker([point.lat, point.lng], {
        icon: pin(point),
        // Leaflet renders markers as focusable elements with this set, so the
        // map is reachable by keyboard rather than being a mouse-only island.
        keyboard: true,
        title: point.title,
        alt: point.title,
        // The selected one on top; the muted ones underneath the rest.
        zIndexOffset: point.tone === 'selected' ? 1000 : point.tone === 'muted' ? -1000 : 0,
      })

      marker.bindTooltip(
        point.subtitle
          ? `<strong>${escapeHtml(point.title)}</strong><br>${escapeHtml(point.subtitle)}`
          : `<strong>${escapeHtml(point.title)}</strong>`,
        { direction: 'top', opacity: 1 },
      )

      marker.on('click', () => options.onSelect?.(point))
      marker.on('keypress', (event) => {
        const key = (event as unknown as { originalEvent: KeyboardEvent }).originalEvent.key
        if (key === 'Enter' || key === ' ') options.onSelect?.(point)
      })

      marker.addTo(layer)
    }

    const key = points.map((p) => `${p.id}@${p.lat},${p.lng}`).sort().join('|')
    if (key === fittedTo) return
    // A picker frames itself once: every click moves the pin, and re-fitting
    // on each one would zoom out from wherever the user had zoomed in to.
    if (options.onPick && fittedTo !== '' && points.length > 0) {
      fittedTo = key
      return
    }
    fittedTo = key

    userMoved = false
    if (points.length === 0) {
      lastBounds = null
      map.setView([mapConfig.fallbackCentre.lat, mapConfig.fallbackCentre.lng], mapConfig.fallbackZoom, {
        animate: false,
      })
      return
    }
    const framed = points.filter((p) => p.frame !== false)
    lastBounds = L.latLngBounds((framed.length ? framed : points).map((p) => [p.lat, p.lng] as L.LatLngTuple))
    // Unanimated: an animation schedules frames that outlive an unmount.
    fit()
  }

  return {
    setPoints,
    resize: () => {
      if (destroyed) return
      map.invalidateSize({ animate: false })
      if (!userMoved) fit()
    },
    destroy: () => {
      if (destroyed) return
      destroyed = true
      map.remove()
    },
  }
}

/** Tooltips and labels take HTML, and a berth name is user-supplied text. */
function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  )
}
